import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { registerDestructor } from '@ember/destroyable';
import { on } from '@ember/modifier';
import { fn, array, hash } from '@ember/helper';
import { eq } from 'tio-ui/utilities';
import { trackedFunction } from 'reactiveweb/function';
import { dropTask } from 'ember-concurrency';
import Button from 'tio-reimagined/components/button';
import Select from 'tio-reimagined/components/select';
import Input from 'tio-reimagined/components/input';
import EmberFlatpickr from 'ember-flatpickr/components/ember-flatpickr';
import PreviewTable from '../report-builder/preview-table';
import { fetchDistinctValues } from '../../utils/distinct-values-resolver';
import type { ColumnAggregationMap, DateGroupingMap } from '../../utils/aggregation-types';
import type { ColumnFormatMap, AppContext } from '../../utils/column-format-options';
import { getReportRequest } from '../../requests/report';
import { SkipCache } from '@warp-drive/core-types/request';
import type Owner from '@ember/owner';
import {
  isDateType as _isDateType,
  isNumericType as _isNumericType,
  isBooleanType as _isBooleanType,
  hasEnumValues as _hasEnumValues,
  getEnumItems as _getEnumItems,
  hasDistinctValues as _hasDistinctValues,
  getDistinctItems as _getDistinctItems,
  isTextType as _isTextType,
  getFilterValue as _getFilterValue,
  getFilterRangeStart as _getFilterRangeStart,
  getFilterRangeEnd as _getFilterRangeEnd,
} from '../../utils/filter-type-helpers';
import {
  resolveFieldsFromEnhancedEndpoint,
  type DatasetConfig,
  type EnhancedFieldsResponse,
} from '../../utils/field-filter-pipeline';
import {
  PhFileText,
  PhDownloadSimple,
  PhArrowLeft,
  PhFunnel,
  PhX,
  PhSlidersHorizontal,
  PhCaretLeft,
} from 'ember-phosphor-icons';
import type StoreService from '@ember-data/store';
import type ReportTrackingService from '../../services/report-tracking';
import type { ReportTrackingSession } from '../../services/report-tracking';
import type { FieldOption } from '../../utils/opensearch-field-mapper';
import {
  getDatasetFromModelType,
  getCompanyFilterField,
  getSearchRequestModelType,
} from '../../utils/dataset-utils';
import {
  getOperatorInfo,
  getOperatorsForCategory,
  type FilterOperator,
} from '../../utils/filter-operators';
import { getRelativeDateItems, getRelativeDateLabel } from '../../utils/relative-date-resolver';

// Local SearchAdapter interface matching adapterFor('search') capabilities.
// Kept local (rather than importing from tio-common/models/search) because the
// shared interface returns OpenSearchResponse<T> which is not structurally
// assignable to the Record<string, unknown> expected by distinct-values-resolver.
interface SearchAdapter {
  search: (query: Record<string, unknown>) => Promise<Record<string, unknown>>;
  fields?: (
    modelType: string,
    companyId?: string,
    programTemplateId?: string
  ) => Promise<EnhancedFieldsResponse>;
  exportReport?: (reportId: string, opts: Record<string, unknown>) => Promise<void>;
}

// Controller interface — reportId and optional companyId travel in the URL
interface ReportsViewerController {
  reportId?: string;
  companyId?: string;
}

interface ReportAttributes {
  name?: string;
  metadata?: Record<string, unknown>;
  'search-config'?: Record<string, unknown>;
  search_config?: Record<string, unknown>;
  searchConfig?: Record<string, unknown>;
  'updated-at'?: string;
  updated_at?: string;
  updatedAt?: string;
}

type CompanyFilterOperator = 'in' | 'not_in';

export interface ReportViewPageSignature {
  Args: {
    controller: ReportsViewerController;
    viewedFrom?: 'admin' | 'partner';
    onBack?: () => void;
  };
}

class ReportViewPage extends Component<ReportViewPageSignature> {
  @service declare store: StoreService;
  @service declare reportTracking: ReportTrackingService;

  private _lastLoadedReportId: string | undefined;
  private reportTrackingSession: ReportTrackingSession | undefined;

  /** localStorage key for persisting user view state per report */
  private _viewStateKey = (reportId: string) => `tio-report-view:${reportId}`;

  get appContext(): AppContext {
    return this.args.viewedFrom === 'partner' ? 'employee' : 'admin';
  }

  /** Save current filter/sort state to localStorage */
  private _saveViewState = () => {
    if (!this.reportId) return;
    try {
      const state: Record<string, unknown> = {};
      if (Object.keys(this.userFilterValues).length > 0) {
        state['filterValues'] = this.userFilterValues;
      }
      if (Object.keys(this.userFilterOperators).length > 0) {
        state['filterOperators'] = this.userFilterOperators;
      }
      if (this.sortField) {
        state['sortField'] = this.sortField;
      }
      if (Object.keys(state).length > 0) {
        localStorage.setItem(this._viewStateKey(this.reportId), JSON.stringify(state));
      } else {
        localStorage.removeItem(this._viewStateKey(this.reportId));
      }
    } catch {
      // localStorage unavailable or quota exceeded — silently ignore
    }
  };

  /** Restore filter/sort state from localStorage, overlaying config defaults */
  private _restoreViewState = () => {
    if (!this.reportId) return;
    try {
      const raw = localStorage.getItem(this._viewStateKey(this.reportId));
      if (!raw) return;
      const state = JSON.parse(raw) as Record<string, unknown>;

      const savedFilters = state['filterValues'] as Record<string, unknown> | undefined;
      if (savedFilters && Object.keys(savedFilters).length > 0) {
        this.userFilterValues = savedFilters;
      }

      const savedOperators = state['filterOperators'] as Record<string, FilterOperator> | undefined;
      if (savedOperators && Object.keys(savedOperators).length > 0) {
        this.userFilterOperators = savedOperators;
      }

      const savedSort = state['sortField'] as string | undefined;
      if (savedSort) {
        this.sortField = savedSort;
      }
    } catch {
      // Corrupted or unavailable — silently ignore
    }
  };

  constructor(owner: Owner, args: ReportViewPageSignature['Args']) {
    super(owner, args);
    registerDestructor(this, this.endReportTrackingSession);
    const id = args.controller.reportId;
    if (id) {
      this._lastLoadedReportId = id;
      this.reportId = id;
      this.loadReport(id);
    }
  }

  // React to reportId query param changes (back/forward nav, in-route transitions)
  _reportIdWatcher = trackedFunction(this, async () => {
    const id = this.args.controller.reportId;
    if (id && id !== this._lastLoadedReportId) {
      this._lastLoadedReportId = id;
      this.reportId = id;
      await this.loadReport(id);
    }
  });

  @tracked reportName = '';
  @tracked updatedAt = '';
  @tracked reportId = '';
  @tracked companyId?: string;
  @tracked companyIds: string[] = [];
  @tracked companyFilterOperator: CompanyFilterOperator = 'in';
  @tracked modelType = 'TasProgramInstance';
  @tracked showIndividualApplicationsOnly = true;
  @tracked datasetConfig: DatasetConfig | undefined;
  @tracked isGlobalReport = false;
  @tracked isLoading = true;
  @tracked isLoadingFields = false;
  @tracked loadError = '';
  @tracked sortField?: string;
  @tracked page = 1;
  @tracked limit = 25;

  // Field metadata for columns and filters
  @tracked allFields: FieldOption[] = [];
  @tracked distinctValuesMap: Record<string, string[]> = {};

  // Parsed from search_config
  @tracked columnKeys: string[] = [];
  @tracked userFilterKeys: string[] = [];
  @tracked buildFilterValues: Record<string, unknown> = {};
  @tracked groupByFieldKeys: string[] = [];
  @tracked columnAggregationMap: ColumnAggregationMap = {};
  @tracked dateGroupingIntervals: DateGroupingMap = {};
  @tracked columnLabelMap: Record<string, string> = {};
  @tracked columnAltTextMap: Record<string, string> = {};
  @tracked columnFormatMap: ColumnFormatMap = {};
  @tracked userFilterValues: Record<string, unknown> = {};
  @tracked userFilterOperators: Record<string, FilterOperator> = {};
  @tracked isFilterSidebarOpen = true;

  // Companies for global report selector
  companiesData = trackedFunction(this, async () => {
    const companies = await this.store.query('company', {
      filter: { 'is-active': 1 },
      page: { limit: 400 },
    });
    return Array.from(companies as Iterable<Record<string, unknown>>).map((c) => ({
      key: c['id'] as string,
      label: (c['name'] as string) || `Company ${c['id']}`,
    }));
  });

  get companies() {
    return this.companiesData.value || [];
  }

  get selectedCompanyIds() {
    return this.companyIds;
  }

  get activeCompanyIds() {
    return this.isGlobalReport ? this.companyIds : [];
  }

  get companyFilterOperatorOptions() {
    return [
      { key: 'in', label: 'one of' },
      { key: 'not_in', label: 'not one of' },
    ];
  }

  /** Whether the report was opened from a company-scoped context (e.g. company page) */
  get contextCompanyId() {
    return this.args.controller.companyId;
  }

  get needsCompanySelector() {
    // Hide company dropdown when viewing from a company-scoped context
    if (this.contextCompanyId) return false;
    return this.isGlobalReport && !this.isLoading;
  }

  get searchRequestModelType() {
    return getSearchRequestModelType(this.modelType, this.showIndividualApplicationsOnly);
  }

  /** Reports need field metadata before we can query */
  get canShowData() {
    if (this.isLoadingFields) return false;
    if (this.loadError) return false;

    return this.allFields.length > 0 && this.selectedColumns.length > 0;
  }

  get controller() {
    return this.args.controller;
  }

  // --- Derived state ---

  /** Merge build filter values (hidden) + user filter values for the query */
  get allFilterValues(): Record<string, unknown> {
    return { ...this.buildFilterValues, ...this.userFilterValues };
  }

  get selectedColumns(): FieldOption[] {
    return this.columnKeys
      .map((key) => this.allFields.find((f) => f.key === key))
      .filter(Boolean) as FieldOption[];
  }

  /** Derive the company filter field from modelType via dataset reverse-mapping */
  get companyFilterField(): string | undefined {
    const dataset = getDatasetFromModelType(this.modelType);
    return (
      this.datasetConfig?.companyFilterField ||
      (dataset ? getCompanyFilterField(dataset) : undefined)
    );
  }

  get isAggregated() {
    return this.groupByFieldKeys.length > 0;
  }

  get userFilters(): FieldOption[] {
    return this.userFilterKeys
      .map((key) => this.allFields.find((f) => f.key === key))
      .filter(Boolean) as FieldOption[];
  }

  get hasUserFilters() {
    return this.userFilters.length > 0;
  }

  get formattedDate() {
    if (!this.updatedAt) return '';
    return new Date(this.updatedAt).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  /** Active filter chips for display below the filter bar */
  get activeFilterChips(): Array<{ key: string; label: string; value: string }> {
    const chips: Array<{ key: string; label: string; value: string }> = [];

    for (const field of this.userFilters) {
      const op = this.detectOperator(field);
      const opLabel = getOperatorInfo(op).label;
      const displayLabel = this.getFieldDisplayLabel(field);

      if (op === 'is_empty' || op === 'is_not_empty') {
        chips.push({ key: field.key, label: displayLabel, value: opLabel });
      } else if (op === 'relative_date') {
        const preset = this.userFilterValues[`${field.key}__relative_date`];
        if (preset) {
          chips.push({
            key: field.key,
            label: displayLabel,
            value: getRelativeDateLabel(String(preset)),
          });
        }
      } else if (op === 'between') {
        const gteVal = this.userFilterValues[`${field.key}__gte`];
        const lteVal = this.userFilterValues[`${field.key}__lte`];
        if (gteVal || lteVal) {
          const parts = [gteVal ? String(gteVal) : '…', lteVal ? String(lteVal) : '…'];
          chips.push({ key: field.key, label: displayLabel, value: `${parts[0]} – ${parts[1]}` });
        }
      } else if (op === 'in' || op === 'not_in') {
        const vals = this.getMultiSelectValues(field);
        if (vals.length > 0) {
          chips.push({
            key: field.key,
            label: `${displayLabel} (${opLabel})`,
            value: vals.join(', '),
          });
        }
      } else {
        const val =
          op === 'eq'
            ? this.userFilterValues[field.key]
            : this.userFilterValues[`${field.key}__${op}`];
        if (val !== undefined && val !== null && val !== '') {
          const prefix = op === 'eq' ? '' : `${opLabel} `;
          chips.push({ key: field.key, label: displayLabel, value: `${prefix}${String(val)}` });
        }
      }
    }
    return chips;
  }

  get hasActiveChips() {
    return this.activeFilterChips.length > 0;
  }

  /** Resolve a field's display label, preferring custom columnLabelMap overrides */
  getFieldDisplayLabel = (field: FieldOption) => {
    return this.columnLabelMap[field.key] || field.label;
  };

  // --- Filter control helpers (delegating to shared utilities) ---

  isDateType = _isDateType;
  isNumericType = _isNumericType;
  isBooleanType = _isBooleanType;
  hasEnumValues = _hasEnumValues;
  getEnumItems = _getEnumItems;
  isTextType = _isTextType;

  hasDistinctValues = (field: FieldOption) => _hasDistinctValues(field, this.distinctValuesMap);

  getDistinctItems = (field: FieldOption) => _getDistinctItems(field, this.distinctValuesMap);

  // --- Operator detection helpers ---

  /** Detect the current operator for a field.\n   * Uses explicitly stored operator if available, otherwise infers from filter value keys. */
  detectOperator = (field: FieldOption): FilterOperator => {
    // Prefer explicitly stored operator
    const stored = this.userFilterOperators[field.key];
    if (stored) return stored;

    // Fallback: infer from filter value keys
    const filters = this.userFilterValues;
    const hasGte =
      filters[`${field.key}__gte`] !== undefined && filters[`${field.key}__gte`] !== null;
    const hasLte =
      filters[`${field.key}__lte`] !== undefined && filters[`${field.key}__lte`] !== null;
    if (hasGte && hasLte) return 'between';

    const suffixes: FilterOperator[] = [
      'relative_date',
      'is_not_empty',
      'is_empty',
      'starts_with',
      'contains',
      'not_in',
      'in',
      'gte',
      'lte',
      'gt',
      'lt',
      'ne',
    ];
    for (const suffix of suffixes) {
      const val = filters[`${field.key}__${suffix}`];
      if (val !== undefined && val !== null) return suffix;
    }
    if (filters[field.key] !== undefined && filters[field.key] !== null) return 'eq';
    return 'eq';
  };

  /** Get display label for the detected operator */
  operatorLabel = (field: FieldOption) => {
    const op = this.detectOperator(field);
    return getOperatorInfo(op).label;
  };

  toggleFilterSidebar = () => {
    this.isFilterSidebarOpen = !this.isFilterSidebarOpen;
  };

  isNoValueOperator = (op: FilterOperator) => op === 'is_empty' || op === 'is_not_empty';
  isBetweenOp = (op: FilterOperator) => op === 'between';
  isMultiSelectOp = (op: FilterOperator) => op === 'in' || op === 'not_in';
  isRelativeDateOp = (op: FilterOperator) => op === 'relative_date';

  /** Get the field category for operator dropdown options */
  getCategory = (field: FieldOption) => {
    if (_isDateType(field)) return 'date' as const;
    if (_isNumericType(field)) return 'number' as const;
    if (_isBooleanType(field)) return 'boolean' as const;
    if (_hasEnumValues(field)) return 'select' as const;
    return 'text' as const;
  };

  /** Get operator select items for a field */
  getOperatorItems = (field: FieldOption) => {
    const category = this.getCategory(field);
    return getOperatorsForCategory(category).map((op) => ({
      key: op.key,
      label: op.label,
    }));
  };

  /** Handle operator change in view mode — clear old values, set new operator */
  handleOperatorChange = (fieldKey: string, newOp: string) => {
    if (!newOp) return;
    const op = newOp as FilterOperator;

    // Clear all existing filter entries for this field
    const vals = { ...this.userFilterValues };
    for (const key of Object.keys(vals)) {
      if (key === fieldKey || key.startsWith(`${fieldKey}__`)) {
        delete vals[key];
      }
    }

    // Set placeholder for new operator
    const info = getOperatorInfo(op);
    if (!info.requiresValue && !info.requiresRange) {
      if (op === 'is_empty') vals[`${fieldKey}__is_empty`] = true;
      else if (op === 'is_not_empty') vals[`${fieldKey}__is_not_empty`] = true;
    }

    this.userFilterValues = vals;

    // Store the explicit operator choice
    this.userFilterOperators = { ...this.userFilterOperators, [fieldKey]: op };

    this.page = 1;
    this._saveViewState();
  };

  /** Relative date preset items for the dropdown */
  relativeDateItems = getRelativeDateItems();

  /** Handle relative date preset selection */
  handleRelativeDateChange = (fieldKey: string, value: string) => {
    this.updateFilterValue(`${fieldKey}__relative_date`, value || undefined);
  };

  /** Get the value for a field using its detected operator */
  getValueForOperator = (field: FieldOption) => {
    const op = this.detectOperator(field);
    if (op === 'eq') return (this.userFilterValues[field.key] as string) || '';
    return (this.userFilterValues[`${field.key}__${op}`] as string) || '';
  };

  /** Get multi-select values for in/not_in operators */
  getMultiSelectValues = (field: FieldOption) => {
    const op = this.detectOperator(field);
    const key = op === 'eq' ? field.key : `${field.key}__${op}`;
    const raw = this.userFilterValues[key];
    if (Array.isArray(raw)) return raw as string[];
    if (typeof raw === 'string' && raw) return raw.split(',');
    return [];
  };

  /** Handle value change for operator-aware filters */
  handleValueForOperator = (fieldKey: string, operator: FilterOperator, value: string) => {
    if (operator === 'eq') {
      this.updateFilterValue(fieldKey, value || undefined);
    } else {
      this.updateFilterValue(`${fieldKey}__${operator}`, value || undefined);
    }
  };

  /** Handle multi-select change for in/not_in operators */
  handleMultiSelectChange = (fieldKey: string, operator: FilterOperator, keys: string[]) => {
    const filterKey = operator === 'eq' ? fieldKey : `${fieldKey}__${operator}`;
    this.updateFilterValue(filterKey, keys.length > 0 ? keys : undefined);
  };

  handleDateValueForOperator = (
    fieldKey: string,
    operator: FilterOperator,
    _dates: Date[],
    dateStr: string
  ) => {
    this.handleValueForOperator(fieldKey, operator, dateStr);
  };

  handleNumberForOperator = (fieldKey: string, operator: FilterOperator, value: string) => {
    const numValue = value ? Number(value) : undefined;
    if (operator === 'eq') {
      this.updateFilterValue(fieldKey, numValue);
    } else {
      this.updateFilterValue(`${fieldKey}__${operator}`, numValue);
    }
  };

  filterWidthStyle = (field: FieldOption) => {
    let maxLen = 0;

    if (_isBooleanType(field)) {
      maxLen = 3; // "Yes" / "No" / "Any"
    } else if (_hasEnumValues(field)) {
      maxLen = Math.max(...(field.enumValues || []).map((v) => v.length), 0);
    } else if (_hasDistinctValues(field, this.distinctValuesMap)) {
      const vals = this.distinctValuesMap[field.key] || [];
      maxLen = Math.max(...vals.map((v) => v.length), 0);
    }

    // Pad for chevron + clear button + internal spacing; floor at 10ch
    const width = Math.max(maxLen + 6, 10);
    return htmlSafe(`min-width: ${width}ch`);
  };

  getFilterValue = (key: string) => _getFilterValue(key, this.userFilterValues);

  getFilterRangeStart = (key: string) => _getFilterRangeStart(key, this.userFilterValues);

  getFilterRangeEnd = (key: string) => _getFilterRangeEnd(key, this.userFilterValues);

  handleSelectChange = (fieldKey: string, value: string) => {
    this.updateFilterValue(fieldKey, value);
  };

  handleDateStartChange = (fieldKey: string, _dates: Date[], dateStr: string) => {
    this.updateFilterValue(`${fieldKey}__gte`, dateStr || undefined);
  };

  handleDateEndChange = (fieldKey: string, _dates: Date[], dateStr: string) => {
    this.updateFilterValue(`${fieldKey}__lte`, dateStr || undefined);
  };

  handleNumberMinChange = (fieldKey: string, value: string) => {
    this.updateFilterValue(`${fieldKey}__gte`, value);
  };

  handleNumberMaxChange = (fieldKey: string, value: string) => {
    this.updateFilterValue(`${fieldKey}__lte`, value);
  };

  handleBooleanChange = (fieldKey: string, value: string) => {
    if (value === 'true') {
      this.updateFilterValue(fieldKey, true);
    } else if (value === 'false') {
      this.updateFilterValue(fieldKey, false);
    } else {
      this.updateFilterValue(fieldKey, '');
    }
  };

  /** Clear all values for a filter (used by chip dismiss) */
  dismissChip = (chipKey: string) => {
    const vals = { ...this.userFilterValues };
    // Remove all operator-suffixed keys for this field
    for (const key of Object.keys(vals)) {
      if (key === chipKey || key.startsWith(`${chipKey}__`)) {
        delete vals[key];
      }
    }
    this.userFilterValues = vals;
    this.page = 1;
    this._saveViewState();

    // Refresh cascading distinct values
    for (const filterKey of this.userFilterKeys) {
      const field = this.allFields.find((f) => f.key === filterKey);
      if (field) this.resolveDistinctValues(field);
    }
  };

  // --- Actions ---

  loadReport = async (reportId: string) => {
    this.isLoading = true;
    this.loadError = '';
    this.reportName = '';
    this.updatedAt = '';
    this.companyId = undefined;
    this.companyIds = [];
    this.companyFilterOperator = 'in';
    this.modelType = 'TasProgramInstance';
    this.showIndividualApplicationsOnly = true;
    this.datasetConfig = undefined;
    this.isGlobalReport = false;
    this.isLoadingFields = false;
    this.columnKeys = [];
    this.userFilterKeys = [];
    this.buildFilterValues = {};
    this.groupByFieldKeys = [];
    this.columnAggregationMap = {};
    this.dateGroupingIntervals = {};
    this.columnLabelMap = {};
    this.columnAltTextMap = {};
    this.columnFormatMap = {};
    this.allFields = [];
    this.distinctValuesMap = {};
    this.sortField = undefined;
    this.userFilterValues = {};
    this.userFilterOperators = {};
    this.page = 1;
    this.limit = 25;
    try {
      const request = getReportRequest(reportId);
      request.cacheOptions = { [SkipCache]: true };
      const response = await this.store.request(request);
      const responseData = response.content as {
        data: { id: string; attributes: ReportAttributes };
      };
      const attrs = responseData?.data?.attributes;
      if (!attrs) return;

      this.reportName = (attrs['name'] as string) || 'Untitled Report';
      this.updatedAt = (attrs['updated-at'] ||
        attrs['updated_at'] ||
        attrs['updatedAt'] ||
        '') as string;

      // Try all possible key formats for the search config
      let rawConfig =
        attrs['search-config'] ?? attrs['search_config'] ?? attrs['searchConfig'] ?? {};
      // If the API returned the config as a JSON string, parse it
      if (typeof rawConfig === 'string') {
        try {
          rawConfig = JSON.parse(rawConfig);
        } catch {
          rawConfig = {};
        }
      }
      const config = rawConfig as Record<string, unknown>;

      this.companyId = (config['companyId'] ?? config['company_id']) as string | undefined;

      // When opened from a company-scoped context, lock to that company
      if (this.contextCompanyId) {
        this.companyId = this.contextCompanyId;
      }

      this.modelType = (config['modelType'] ??
        config['model_type'] ??
        'TasProgramInstance') as string;
      const savedIndividualApplicationsOnly =
        config['showIndividualApplicationsOnly'] ?? config['show_individual_applications_only'];
      this.showIndividualApplicationsOnly =
        typeof savedIndividualApplicationsOnly === 'boolean'
          ? savedIndividualApplicationsOnly
          : this.modelType === 'TasProgramInstance';
      this.isGlobalReport = !this.companyId;

      // Parse table columns (handle camelCase and snake_case keys)
      const tableSettings = (config['tableSettings'] || config['table_settings']) as
        | Record<string, unknown>
        | undefined;
      if (tableSettings) {
        const cols = (tableSettings['selectedColumns'] || tableSettings['selected_columns']) as
          | string[]
          | undefined;
        if (cols?.length) this.columnKeys = cols;

        const sortBy = (tableSettings['sortBy'] || tableSettings['sort_by']) as string | undefined;
        const sortDir =
          ((tableSettings['sortDirection'] || tableSettings['sort_direction']) as string) || 'asc';
        if (sortBy) this.sortField = `${sortBy}:${sortDir}`;

        const savedLabels = (tableSettings['columnLabels'] || tableSettings['column_labels']) as
          | Record<string, string>
          | undefined;
        if (savedLabels && Object.keys(savedLabels).length > 0) {
          this.columnLabelMap = savedLabels;
        }

        const savedAltTexts = (tableSettings['columnAltTexts'] ||
          tableSettings['column_alt_texts']) as Record<string, string> | undefined;
        if (savedAltTexts && Object.keys(savedAltTexts).length > 0) {
          this.columnAltTextMap = savedAltTexts;
        }

        const savedFormats = (tableSettings['columnFormats'] || tableSettings['column_formats']) as
          | ColumnFormatMap
          | undefined;
        if (savedFormats && Object.keys(savedFormats).length > 0) {
          this.columnFormatMap = savedFormats;
        }
      }

      // Parse build filters (applied silently)
      const buildFilters = (config['buildFilters'] || config['build_filters']) as
        | Record<string, unknown>
        | undefined;
      if (buildFilters) {
        const bfv = (buildFilters['filterValues'] || buildFilters['filter_values']) as
          | Record<string, unknown>
          | undefined;
        if (bfv && Object.keys(bfv).length > 0) {
          this.buildFilterValues = bfv;
        }
      }

      // Parse user filters (shown in UI)
      const userFilters = (config['userFilters'] || config['user_filters']) as
        | Record<string, unknown>
        | undefined;
      if (userFilters) {
        const uf = (userFilters['selectedFilters'] || userFilters['selected_filters']) as
          | string[]
          | undefined;
        if (uf?.length) this.userFilterKeys = uf;

        // Restore saved user filter values
        const ufv = (userFilters['filterValues'] || userFilters['filter_values']) as
          | Record<string, unknown>
          | undefined;
        if (ufv && Object.keys(ufv).length > 0) {
          this.userFilterValues = ufv;
        }

        // Restore saved user filter operators
        const ufo = (userFilters['filterOperators'] || userFilters['filter_operators']) as
          | Record<string, FilterOperator>
          | undefined;
        if (ufo && Object.keys(ufo).length > 0) {
          this.userFilterOperators = ufo;
        }
      }

      // Parse group-by config
      const groupBy = (config['groupBy'] || config['group_by']) as
        | Record<string, unknown>
        | undefined;
      if (groupBy) {
        const gbFields = (groupBy['selectedFields'] || groupBy['selected_fields']) as
          | string[]
          | undefined;
        if (gbFields?.length) this.groupByFieldKeys = gbFields;

        const colAggs = (groupBy['columnAggregations'] || groupBy['column_aggregations']) as
          | ColumnAggregationMap
          | undefined;
        if (colAggs && Object.keys(colAggs).length > 0) this.columnAggregationMap = colAggs;

        const dateIntervals = (groupBy['dateGroupingIntervals'] ||
          groupBy['date_grouping_intervals']) as DateGroupingMap | undefined;
        if (dateIntervals && Object.keys(dateIntervals).length > 0)
          this.dateGroupingIntervals = dateIntervals;
      }

      // Overlay with user's locally-saved view state (filters + sort)
      this._restoreViewState();

      this.isLoading = false;
      await this.loadFields();

      if (this.isDestroying || this.isDestroyed) return;

      this.endReportTrackingSession();
      this.reportTrackingSession = this.reportTracking.startSession(
        reportId,
        this.args.viewedFrom ?? 'admin'
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred';
      this.loadError = `Failed to retrieve report: ${message}`;
    } finally {
      this.isLoading = false;
    }
  };

  private endReportTrackingSession = () => {
    this.reportTrackingSession?.end();
    this.reportTrackingSession = undefined;
  };

  loadFields = async () => {
    const searchAdapter = this.store.adapterFor?.('search') as SearchAdapter | undefined;
    this.isLoadingFields = true;
    this.loadError = '';

    try {
      if (!searchAdapter?.fields) {
        this.allFields = [];
        this.loadError = 'Unable to load report fields.';
        return;
      }

      const companyId = this.companyId || undefined;

      const response = await searchAdapter.fields(this.searchRequestModelType, companyId);
      this.datasetConfig = response.datasetConfig;

      this.allFields = resolveFieldsFromEnhancedEndpoint(response);

      // Resolve distinct values for all text/keyword user filters at runtime
      for (const key of this.userFilterKeys) {
        const field = this.allFields.find((f) => f.key === key);
        if (field) this.resolveDistinctValues(field);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred';
      this.allFields = [];
      this.loadError = `Failed to load report fields: ${message}`;
    } finally {
      this.isLoadingFields = false;
    }
  };

  resolveDistinctValues = (field: FieldOption) => {
    const isTextField = field.type === 'keyword' || field.type === 'text';
    if (!isTextField) return;

    const searchAdapter = this.store.adapterFor?.('search') as SearchAdapter | undefined;
    if (!searchAdapter) return;

    fetchDistinctValues(
      searchAdapter,
      field.key,
      this.companyId,
      this.allFilterValues,
      this.searchRequestModelType
    )
      .then((values) => {
        if (values) {
          this.distinctValuesMap = { ...this.distinctValuesMap, [field.key]: values };
        } else {
          const next = { ...this.distinctValuesMap };
          delete next[field.key];
          this.distinctValuesMap = next;
        }
      })
      .catch(() => {
        // Distinct value fetch failed — field will fall back to text input
      });
  };

  updateFilterValue = (key: string, value: unknown) => {
    const vals = { ...this.userFilterValues, [key]: value };
    for (const [k, v] of Object.entries(vals)) {
      if (v === '' || v === undefined || v === null) {
        delete vals[k];
      }
    }
    this.userFilterValues = vals;
    this.page = 1;
    this._saveViewState();

    // Track filter interaction
    const baseKey = key.replace(
      /__(gte|lte|gt|lt|ne|in|not_in|is_empty|is_not_empty|contains|starts_with|relative_date)$/,
      ''
    );
    const fieldLabel =
      this.columnLabelMap[baseKey] ||
      this.allFields.find((f) => f.key === baseKey)?.label ||
      baseKey;
    this.reportTracking.trackFilter(key, value, fieldLabel);

    // Refresh cascading distinct values
    for (const filterKey of this.userFilterKeys) {
      const field = this.allFields.find((f) => f.key === filterKey);
      if (field) this.resolveDistinctValues(field);
    }
  };

  removeFilter = (_field: FieldOption) => {
    // no-op in viewer — filters are fixed; values are cleared via dismissChip
  };

  updateSort = (sortField: string) => {
    this.sortField = sortField;
    this._saveViewState();

    // Track sort interaction
    const [field = '', direction = 'asc'] = sortField.split(':');
    const sortLabel = this.allFields.find((f) => f.key === field)?.label || field;
    this.reportTracking.trackSort(field, direction, sortLabel);
  };

  updatePage = (page: number) => {
    this.page = page;
  };

  updateLimit = (limit: number) => {
    this.limit = limit;
    this.page = 1;
  };

  handleBack = () => {
    if (this.args.onBack) {
      this.args.onBack();
    }
  };

  exportReport = dropTask(async () => {
    if (!this.reportId) return;

    this.reportTracking.trackExport('csv');

    const searchAdapter = this.store.adapterFor?.('search') as SearchAdapter | undefined;
    if (!searchAdapter?.exportReport) return;

    try {
      await searchAdapter.exportReport(this.reportId, {
        companyId: this.companyId,
      });
    } catch (error) {
      console.error('Export failed:', error);
    }
  });

  handleCompanyIdsChange = (companyIds: string[]) => {
    this.companyIds = companyIds;
    this.page = 1;
    this.distinctValuesMap = {};
  };

  handleCompanyFilterOperatorChange = (operator: string) => {
    this.companyFilterOperator = operator === 'not_in' ? 'not_in' : 'in';
    this.page = 1;
  };

  <template>
    {{! Consume watcher to keep it reactive }}
    {{this._reportIdWatcher.value}}
    <div class="flex flex-col h-full min-h-screen bg-gray-50 dark:bg-gray-900">
      {{! Header }}
      <header
        class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            {{#if @onBack}}
              <button
                type="button"
                class="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                title="Back to reports"
                {{on "click" this.handleBack}}
              >
                <PhArrowLeft @size={{20}} />
              </button>
            {{/if}}
            <PhFileText @size={{24}} class="text-gray-600 dark:text-gray-400" />
            <h1 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {{this.reportName}}
            </h1>
            {{#if this.formattedDate}}
              <span class="text-sm text-gray-500 dark:text-gray-400">
                Updated
                {{this.formattedDate}}
              </span>
            {{/if}}
          </div>
          <div class="flex items-center gap-3">
            {{#if this.exportReport.isRunning}}
              <span
                class="inline-flex items-center gap-2 text-sm font-medium text-ocean-600 dark:text-ocean-400 animate-pulse"
              >
                <span
                  class="h-2 w-2 rounded-full bg-ocean-500 animate-ping"
                  aria-hidden="true"
                ></span>
                Generating export…
              </span>
            {{/if}}
            <Button
              @appearance="outlined"
              @intent="primary"
              @icon={{PhDownloadSimple}}
              @isRunning={{this.exportReport.isRunning}}
              {{on "click" this.exportReport.perform}}
            >
              {{if this.exportReport.isRunning "Exporting..." "Export"}}
            </Button>
          </div>
        </div>
      </header>

      <div class="flex-1 overflow-hidden">
        {{#if this.loadError}}
          <div class="p-6">
            <div
              class="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-300 text-sm"
            >
              {{this.loadError}}
            </div>
          </div>
        {{else if this.isLoading}}
          <div
            class="flex items-center justify-center h-48 text-gray-400 dark:text-gray-500 text-sm"
          >
            Loading report...
          </div>
        {{else}}
          <div class="flex h-full">
            {{! ── Filter Sidebar ── }}
            {{#if this.hasUserFilters}}
              <div
                class="shrink-0 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex flex-col transition-all duration-200
                  {{if this.isFilterSidebarOpen 'w-72' 'w-10'}}"
              >
                {{! Sidebar header with toggle }}
                <div
                  class="flex items-center justify-between px-3 py-3 border-b border-gray-100 dark:border-gray-700"
                >
                  {{#if this.isFilterSidebarOpen}}
                    <div
                      class="flex items-center gap-1.5 text-sm font-semibold text-gray-600 dark:text-gray-300"
                    >
                      <PhFunnel @size={{14}} />
                      <span>Filters</span>
                    </div>
                  {{/if}}
                  <button
                    type="button"
                    class="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 dark:text-gray-500"
                    aria-label={{if this.isFilterSidebarOpen "Collapse filters" "Expand filters"}}
                    aria-expanded={{if this.isFilterSidebarOpen "true" "false"}}
                    {{on "click" this.toggleFilterSidebar}}
                  >
                    {{#if this.isFilterSidebarOpen}}
                      <PhCaretLeft @size={{14}} />
                    {{else}}
                      <PhSlidersHorizontal @size={{16}} />
                    {{/if}}
                  </button>
                </div>

                {{! Filter controls (visible when open) }}
                {{#if this.isFilterSidebarOpen}}
                  <div class="flex-1 overflow-y-auto p-3 space-y-4">
                    {{#each this.userFilters as |field|}}
                      {{#let (this.detectOperator field) as |currentOp|}}
                        <div class="space-y-1.5">
                          <span
                            class="text-xs font-medium text-gray-500 dark:text-gray-400"
                          >{{this.getFieldDisplayLabel field}}</span>
                          <Select
                            @items={{this.getOperatorItems field}}
                            @selected={{currentOp}}
                            @onChange={{fn this.handleOperatorChange field.key}}
                            @size="sm"
                            @searchable={{false}}
                            @clearable={{false}}
                          />

                          {{! Value input — varies by operator }}
                          {{#if (this.isNoValueOperator currentOp)}}
                            <p class="text-xs text-gray-400 dark:text-gray-500 italic">
                              {{if
                                (eq currentOp "is_empty")
                                "Matches records with no value"
                                "Matches records with a value"
                              }}
                            </p>
                          {{else if (this.isRelativeDateOp currentOp)}}
                            <Select
                              @items={{this.relativeDateItems}}
                              @selected={{this.getValueForOperator field}}
                              @onChange={{fn this.handleRelativeDateChange field.key}}
                              @placeholder="Select a time range…"
                              @size="sm"
                              @clearable={{true}}
                              @searchable={{true}}
                            />
                          {{else if (this.isBetweenOp currentOp)}}
                            {{#if (this.isDateType field)}}
                              <div class="space-y-1">
                                {{! @glint-expect-error: Flatpickr type mismatch }}
                                <EmberFlatpickr
                                  @date={{if
                                    (this.getFilterRangeStart field.key)
                                    (this.getFilterRangeStart field.key)
                                    ""
                                  }}
                                  @onChange={{fn this.handleDateStartChange field.key}}
                                  @dateFormat="Y-m-d"
                                  @altInput={{true}}
                                  @altFormat="M j, Y"
                                  placeholder="Start date"
                                  class="tio-form-input text-sm w-full"
                                />
                                {{! @glint-expect-error: Flatpickr type mismatch }}
                                <EmberFlatpickr
                                  @date={{if
                                    (this.getFilterRangeEnd field.key)
                                    (this.getFilterRangeEnd field.key)
                                    ""
                                  }}
                                  @onChange={{fn this.handleDateEndChange field.key}}
                                  @dateFormat="Y-m-d"
                                  @altInput={{true}}
                                  @altFormat="M j, Y"
                                  @minDate={{if
                                    (this.getFilterRangeStart field.key)
                                    (this.getFilterRangeStart field.key)
                                    ""
                                  }}
                                  placeholder="End date"
                                  class="tio-form-input text-sm w-full"
                                />
                              </div>
                            {{else}}
                              <div class="space-y-1">
                                <Input
                                  @type="number"
                                  @size="sm"
                                  @value={{this.getFilterRangeStart field.key}}
                                  @onChange={{fn this.handleNumberMinChange field.key}}
                                  @placeholder="Min"
                                />
                                <Input
                                  @type="number"
                                  @size="sm"
                                  @value={{this.getFilterRangeEnd field.key}}
                                  @onChange={{fn this.handleNumberMaxChange field.key}}
                                  @placeholder="Max"
                                />
                              </div>
                            {{/if}}
                          {{else if (this.isMultiSelectOp currentOp)}}
                            {{#if (this.hasDistinctValues field)}}
                              <Select
                                @items={{this.getDistinctItems field}}
                                @multiple={{true}}
                                @selectedKeys={{this.getMultiSelectValues field}}
                                @onChangeKeys={{fn
                                  this.handleMultiSelectChange
                                  field.key
                                  currentOp
                                }}
                                @placeholder="Select values…"
                                @size="sm"
                                @clearable={{true}}
                                @searchable={{true}}
                              />
                            {{else if (this.hasEnumValues field)}}
                              <Select
                                @items={{this.getEnumItems field}}
                                @multiple={{true}}
                                @selectedKeys={{this.getMultiSelectValues field}}
                                @onChangeKeys={{fn
                                  this.handleMultiSelectChange
                                  field.key
                                  currentOp
                                }}
                                @placeholder="Select values…"
                                @size="sm"
                                @clearable={{true}}
                                @searchable={{true}}
                              />
                            {{else}}
                              <Input
                                @size="sm"
                                @value={{this.getValueForOperator field}}
                                @onChange={{fn this.handleValueForOperator field.key currentOp}}
                                @placeholder="Comma-separated values…"
                              />
                            {{/if}}
                          {{else if (this.isBooleanType field)}}
                            <Select
                              @items={{array
                                (hash key="true" label="Yes")
                                (hash key="false" label="No")
                              }}
                              @selected={{this.getFilterValue field.key}}
                              @onChange={{fn this.handleBooleanChange field.key}}
                              @placeholder="Any"
                              @size="sm"
                              @clearable={{true}}
                            />
                          {{else if (this.hasEnumValues field)}}
                            <Select
                              @items={{this.getEnumItems field}}
                              @selected={{this.getValueForOperator field}}
                              @onChange={{fn this.handleValueForOperator field.key currentOp}}
                              @placeholder="All"
                              @size="sm"
                              @clearable={{true}}
                              @searchable={{true}}
                            />
                          {{else if (this.isDateType field)}}
                            {{! @glint-expect-error: Flatpickr type mismatch }}
                            <EmberFlatpickr
                              @date={{if
                                (this.getValueForOperator field)
                                (this.getValueForOperator field)
                                ""
                              }}
                              @onChange={{fn this.handleDateValueForOperator field.key currentOp}}
                              @dateFormat="Y-m-d"
                              @altInput={{true}}
                              @altFormat="M j, Y"
                              placeholder="Select a date"
                              class="tio-form-input text-sm w-full"
                            />
                          {{else if (this.isNumericType field)}}
                            <Input
                              @type="number"
                              @size="sm"
                              @value={{this.getValueForOperator field}}
                              @onChange={{fn this.handleNumberForOperator field.key currentOp}}
                              @placeholder="Enter a number…"
                            />
                          {{else if (this.isTextType field)}}
                            {{#if (this.hasDistinctValues field)}}
                              <Select
                                @items={{this.getDistinctItems field}}
                                @selected={{this.getValueForOperator field}}
                                @onChange={{fn this.handleValueForOperator field.key currentOp}}
                                @placeholder="All"
                                @size="sm"
                                @clearable={{true}}
                                @searchable={{true}}
                              />
                            {{else}}
                              <Input
                                @size="sm"
                                @value={{this.getValueForOperator field}}
                                @onChange={{fn this.handleValueForOperator field.key currentOp}}
                                @placeholder="Enter value…"
                              />
                            {{/if}}
                          {{else}}
                            <Input
                              @size="sm"
                              @value={{this.getValueForOperator field}}
                              @onChange={{fn this.handleValueForOperator field.key currentOp}}
                              @placeholder="Enter value…"
                            />
                          {{/if}}
                        </div>
                      {{/let}}
                    {{/each}}
                  </div>
                {{/if}}
              </div>
            {{/if}}

            {{! ── Main Content ── }}
            <div class="flex-1 overflow-auto p-6 space-y-4">
              {{! Company Selector for global reports }}
              {{#if this.needsCompanySelector}}
                <div
                  class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
                >
                  <div class="flex items-end gap-3 flex-wrap">
                    <div class="flex flex-col gap-1 w-36">
                      <span
                        class="text-xs font-medium text-gray-500 dark:text-gray-400"
                      >Company</span>
                      <Select
                        @items={{this.companyFilterOperatorOptions}}
                        @selected={{this.companyFilterOperator}}
                        @onChange={{this.handleCompanyFilterOperatorChange}}
                        @size="sm"
                        @clearable={{false}}
                      />
                    </div>
                    <div class="flex flex-col gap-1 min-w-80 max-w-xl flex-1">
                      <span
                        class="text-xs font-medium text-gray-500 dark:text-gray-400"
                      >Companies</span>
                      <Select
                        @items={{this.companies}}
                        @multiple={{true}}
                        @selectedKeys={{this.selectedCompanyIds}}
                        @onChangeKeys={{this.handleCompanyIdsChange}}
                        @placeholder="All companies"
                        @size="sm"
                        @searchable={{true}}
                        @clearable={{true}}
                      />
                    </div>
                  </div>
                </div>
              {{/if}}

              {{! Active filter chips }}
              {{#if this.hasActiveChips}}
                <div class="flex flex-wrap gap-2">
                  {{#each this.activeFilterChips as |chip|}}
                    <span
                      class="inline-flex items-center gap-1 bg-teal-50 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200 rounded-full px-3 py-1 text-sm"
                    >
                      <span class="font-medium">{{chip.label}}:</span>
                      <span>{{chip.value}}</span>
                      <button
                        type="button"
                        class="ml-0.5 p-0.5 rounded-full hover:bg-teal-100 hover:text-teal-900 dark:hover:bg-teal-800 dark:hover:text-teal-100"
                        title="Clear filter"
                        {{on "click" (fn this.dismissChip chip.key)}}
                      >
                        <PhX @size={{12}} />
                      </button>
                    </span>
                  {{/each}}
                </div>
              {{/if}}

              {{! Data Table or loading/empty states }}
              {{#if this.isLoadingFields}}
                <div
                  class="flex items-center justify-center h-48 text-gray-400 dark:text-gray-500 text-sm"
                >
                  Loading report data...
                </div>
              {{else if this.canShowData}}
                <PreviewTable
                  @columns={{this.selectedColumns}}
                  @allFields={{this.allFields}}
                  @filterValues={{this.allFilterValues}}
                  @companyId={{this.companyId}}
                  @companyIds={{this.activeCompanyIds}}
                  @companyFilterOperator={{this.companyFilterOperator}}
                  @companyFilterField={{this.companyFilterField}}
                  @modelType={{this.searchRequestModelType}}
                  @sortField={{this.sortField}}
                  @page={{this.page}}
                  @limit={{this.limit}}
                  @onSort={{this.updateSort}}
                  @onPageChange={{this.updatePage}}
                  @onLimitChange={{this.updateLimit}}
                  @readOnly={{true}}
                  @isAggregated={{this.isAggregated}}
                  @groupByFieldKeys={{this.groupByFieldKeys}}
                  @columnAggregationMap={{this.columnAggregationMap}}
                  @dateGroupingIntervals={{this.dateGroupingIntervals}}
                  @columnFormatMap={{this.columnFormatMap}}
                  @columnLabelMap={{this.columnLabelMap}}
                  @columnAltTextMap={{this.columnAltTextMap}}
                  @appContext={{this.appContext}}
                />
              {{else}}
                <div
                  class="flex items-center justify-center h-48 text-gray-400 dark:text-gray-500 text-sm bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                >
                  Unable to load report data.
                </div>
              {{/if}}
            </div>
          </div>
        {{/if}}
      </div>
    </div>
  </template>
}

export default ReportViewPage;
